import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { AlertCircle, CheckCircle2, Eye, EyeOff, KeyRound, Trash2, XCircle, Zap } from 'lucide-react';
import { formatDistanceToNow } from 'date-fns';
import { tr } from 'date-fns/locale';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Badge } from '../ui/badge';
import { Alert, AlertDescription } from '../ui/alert';
import { Spinner } from '../ui/spinner';
import { toast } from '../ui/sonner';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../ui/alert-dialog';
import { ApiError } from '../../lib/auth/client';
import {
  clearOpenAIAdminKey,
  getPlatformSettings,
  platformSettingsKey,
  setOpenAIAdminKey,
  testOpenAIAdminKey,
} from '../../lib/queries/platformSettings';

export function OpenAIAdminKeyCard() {
  const qc = useQueryClient();
  const [adminKey, setAdminKey] = useState('');
  const [showKey, setShowKey] = useState(false);
  const [confirmClear, setConfirmClear] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const settings = useQuery({ queryKey: platformSettingsKey, queryFn: getPlatformSettings });
  const meta = settings.data?.openai;

  const save = useMutation({
    mutationFn: () => setOpenAIAdminKey({ adminKey }),
    onSuccess: (resp) => {
      toast.success(resp.rotated ? 'Admin anahtarı yenilendi' : 'Admin anahtarı kaydedildi');
      setAdminKey('');
      qc.invalidateQueries({ queryKey: platformSettingsKey });
    },
    onError: (err) => {
      setError(err instanceof ApiError ? err.problem.detail ?? err.problem.title : 'Kayıt başarısız');
    },
  });

  const test = useMutation({
    mutationFn: testOpenAIAdminKey,
    onSuccess: (resp) => {
      qc.invalidateQueries({ queryKey: platformSettingsKey });
      if (resp.ok) toast.success(resp.message ?? 'Admin anahtarı geçerli');
      else toast.error(resp.message ?? `Test başarısız (${resp.providerStatus ?? '-'})`);
    },
    onError: (err) => {
      toast.error(err instanceof ApiError ? err.problem.detail ?? err.problem.title : 'Test başarısız');
    },
  });

  const clear = useMutation({
    mutationFn: clearOpenAIAdminKey,
    onSuccess: () => {
      toast.success('Admin anahtarı temizlendi');
      qc.invalidateQueries({ queryKey: platformSettingsKey });
    },
  });

  const handleSave = (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    if (adminKey.trim().length < 20) {
      setError('Admin anahtarı en az 20 karakter olmalı');
      return;
    }
    save.mutate();
  };

  return (
    <>
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <KeyRound className="h-5 w-5" /> OpenAI Admin Anahtarı
          </CardTitle>
          <CardDescription>
            Platform geneli admin anahtarı (sk-admin-…). Tüm müşterilerin OpenAI kullanımı bu anahtar
            ve project_id filtresi ile çekilir. Müşterilerin kendi anahtarları sistemde tutulmaz.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-center gap-2">
            {meta?.configured ? (
              <Badge variant="success" className="gap-1">
                <CheckCircle2 className="h-3 w-3" /> Yapılandırıldı
              </Badge>
            ) : (
              <Badge variant="secondary" className="gap-1">
                <XCircle className="h-3 w-3" /> Yapılandırılmadı
              </Badge>
            )}
            {meta?.lastValidatedStatus === 'ok' && (
              <Badge variant="outline" className="gap-1 border-emerald-300 text-emerald-700">
                Doğrulandı
                {meta.lastValidatedAt &&
                  ` ${formatDistanceToNow(new Date(meta.lastValidatedAt), { addSuffix: true, locale: tr })}`}
              </Badge>
            )}
            {meta?.lastValidatedStatus === 'failed' && (
              <Badge variant="destructive" className="gap-1">
                Son test başarısız
              </Badge>
            )}
          </div>

          <form onSubmit={handleSave} className="space-y-3" noValidate>
            {error && (
              <Alert variant="destructive">
                <AlertCircle className="h-4 w-4" />
                <AlertDescription>{error}</AlertDescription>
              </Alert>
            )}
            <div className="space-y-2">
              <Label htmlFor="adminKey">
                {meta?.configured ? 'Yeni admin anahtarı (yenile)' : 'Admin anahtarı'}
              </Label>
              <div className="relative">
                <Input
                  id="adminKey"
                  type={showKey ? 'text' : 'password'}
                  value={adminKey}
                  onChange={(e) => setAdminKey(e.target.value)}
                  placeholder="sk-admin-..."
                  autoComplete="off"
                />
                <button
                  type="button"
                  aria-label={showKey ? 'Gizle' : 'Göster'}
                  onClick={() => setShowKey((s) => !s)}
                  className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-500 hover:text-slate-900"
                >
                  {showKey ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </button>
              </div>
              <p className="text-xs text-slate-500">
                Düz metin anahtar kaydedildikten sonra bir daha gösterilmez. Envelope encryption (KEK + DEK) ile saklanır.
              </p>
            </div>
            <div className="flex flex-wrap gap-2">
              <Button type="submit" disabled={save.isPending || adminKey.trim().length === 0}>
                {save.isPending ? (
                  <>
                    <Spinner /> Kaydediliyor…
                  </>
                ) : meta?.configured ? (
                  'Yenile'
                ) : (
                  'Kaydet'
                )}
              </Button>
              <Button
                type="button"
                variant="outline"
                onClick={() => test.mutate()}
                disabled={!meta?.configured || test.isPending}
              >
                {test.isPending ? <Spinner /> : <Zap className="h-4 w-4" />} Test Et
              </Button>
              <Button
                type="button"
                variant="ghost"
                className="text-rose-600 hover:bg-rose-50 hover:text-rose-700"
                onClick={() => setConfirmClear(true)}
                disabled={!meta?.configured || clear.isPending}
              >
                <Trash2 className="h-4 w-4" /> Temizle
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>

      <AlertDialog open={confirmClear} onOpenChange={setConfirmClear}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Admin anahtarı silinsin mi?</AlertDialogTitle>
            <AlertDialogDescription>
              Silindikten sonra tüm OpenAI kullanım çekme işlemleri (test + worker) hata verir. Yeni
              bir admin anahtarı kaydedilene kadar geçerli olur.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>İptal</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => {
                clear.mutate();
                setConfirmClear(false);
              }}
            >
              Sil
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
